import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createForm, updateForm } from '../../lib/api';
import type { FormType, ProductSettings, OrderSettings } from '../../types/form';

interface MobileFormCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onFormCreated?: () => void;
}

export default function MobileFormCreationWizard({ isOpen, onClose, onFormCreated }: MobileFormCreationWizardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('mobile');
  const [step, setStep] = useState<'type' | 'name'>('type');
  const [formType, setFormType] = useState<FormType>('form');
  const [formName, setFormName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTypeSelect = (type: FormType) => {
    setFormType(type);
    setStep('name');
  };

  const handleBack = () => {
    if (step === 'name') {
      setStep('type');
    } else {
      onClose();
    }
  };

  const handleCreate = async () => {
    if (!formName.trim()) {
      setError(t('createWizard.nameRequired'));
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // Build initial schema based on form type
      const initialSchema: Record<string, unknown> = {
        version: 1,
        fields: [],
        steps: [],
        conditionalLogic: [],
        settings: {
          submitButtonText: 'Submit',
          successMessage: 'Thank you for your submission!',
          theme: 'light',
          submitActions: [{ type: 'database' }],
        },
      };

      // Add form type specific settings
      if (formType === 'poll') {
        initialSchema.pollSettings = {
          enabled: true,
          allowMultipleVotes: false,
          showResultsAfterVote: true,
          anonymousVoting: true,
        };
      } else if (formType === 'quiz') {
        (initialSchema.settings as Record<string, unknown>).quiz = {
          enabled: true,
          showScore: true,
          showScorePercentage: true,
          showCorrectAnswers: true,
          showCorrectAnswersWhen: 'immediately',
          passingScore: 70,
          showPointsPerQuestion: true,
          randomizeQuestions: false,
          randomizeOptions: false,
          allowRetake: true,
          maxAttempts: 0,
        };
      } else if (formType === 'live_quiz') {
        (initialSchema.settings as Record<string, unknown>).quiz = {
          enabled: true,
          mode: 'live',
          showScore: true,
          showScorePercentage: true,
          showCorrectAnswers: true,
          showCorrectAnswersWhen: 'immediately',
          passingScore: 0,
          showPointsPerQuestion: true,
          randomizeQuestions: false,
          randomizeOptions: true,
          allowRetake: false,
          maxAttempts: 1,
          liveSettings: {
            questionTimeSeconds: 20,
            showQuestionOnPlayerDevice: true,
            enableStreakBonus: true,
            streakBonusMultiplier: 1.2,
            streakThreshold: 3,
            timeBasedScoring: true,
            maxPointsPerQuestion: 1000,
            minPointsPerQuestion: 500,
            showLeaderboardAfterQuestion: true,
            leaderboardSize: 5,
            showAnswerDistribution: true,
            showCorrectAnswer: true,
            allowLateJoin: false,
            autoAdvance: false,
            autoAdvanceDelaySeconds: 5,
            randomizeQuestions: false,
            randomizeOptions: true,
            enableSounds: true,
          },
        };
      } else if (formType === 'product') {
        const productSettings: ProductSettings = {
          enabled: true,
          currency: 'USD',
          trackInventory: false,
          allowBackorders: false,
          requiresShipping: true,
          isActive: true,
        };
        (initialSchema.settings as Record<string, unknown>).product = productSettings;
      } else if (formType === 'order') {
        const orderSettings: OrderSettings = {
          enabled: true,
          orderNumberPrefix: 'ORD-',
          orderNumberStart: 1001,
          requirePayment: true,
          paymentMethods: ['stripe'],
          collectShippingAddress: true,
          collectBillingAddress: false,
          guestCheckout: true,
          sendOrderConfirmation: true,
        };
        (initialSchema.settings as Record<string, unknown>).order = orderSettings;
      }

      // Create the form
      const result = await createForm({
        name: formName.trim(),
      });

      // Update form with initial schema settings
      await updateForm(result.id, {
        schema: initialSchema as unknown as Parameters<typeof updateForm>[1]['schema'],
      });

      onFormCreated?.();
      onClose();

      // Navigate to the appropriate builder based on form type
      if (formType === 'live_quiz') {
        navigate(`/m/live-quiz/${result.id}`);
      } else if (formType === 'product') {
        navigate(`/m/products/${result.id}`);
      } else if (formType === 'order') {
        navigate(`/m/orders/${result.id}`);
      } else {
        navigate(`/m/builder/${result.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('createWizard.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const resetAndClose = () => {
    setStep('type');
    setFormType('form');
    setFormName('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const formTypes = [
    {
      type: 'form' as FormType,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'blue',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      textColor: 'text-blue-600 dark:text-blue-400',
      borderColor: 'border-blue-500',
      ringColor: 'ring-blue-200 dark:ring-blue-800',
    },
    {
      type: 'poll' as FormType,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      color: 'purple',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      textColor: 'text-purple-600 dark:text-purple-400',
      borderColor: 'border-purple-500',
      ringColor: 'ring-purple-200 dark:ring-purple-800',
    },
    {
      type: 'quiz' as FormType,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      color: 'green',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      textColor: 'text-green-600 dark:text-green-400',
      borderColor: 'border-green-500',
      ringColor: 'ring-green-200 dark:ring-green-800',
    },
    {
      type: 'live_quiz' as FormType,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      color: 'orange',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
      textColor: 'text-orange-600 dark:text-orange-400',
      borderColor: 'border-orange-500',
      ringColor: 'ring-orange-200 dark:ring-orange-800',
      badge: t('createWizard.interactive'),
    },
    {
      type: 'product' as FormType,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      color: 'teal',
      bgColor: 'bg-teal-100 dark:bg-teal-900/30',
      textColor: 'text-teal-600 dark:text-teal-400',
      borderColor: 'border-teal-500',
      ringColor: 'ring-teal-200 dark:ring-teal-800',
      badge: t('createWizard.ecommerce', 'E-commerce'),
    },
    {
      type: 'order' as FormType,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      color: 'pink',
      bgColor: 'bg-pink-100 dark:bg-pink-900/30',
      textColor: 'text-pink-600 dark:text-pink-400',
      borderColor: 'border-pink-500',
      ringColor: 'ring-pink-200 dark:ring-pink-800',
      badge: t('createWizard.ecommerce', 'E-commerce'),
    },
  ];

  const selectedTypeConfig = formTypes.find(ft => ft.type === formType);

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 animate-fadeIn" onClick={resetAndClose} />

      {/* Bottom Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom max-h-[90vh] flex flex-col">
        {/* Handle */}
        <div className="flex justify-center py-3 flex-shrink-0">
          <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 pb-4 flex items-center justify-between flex-shrink-0">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg touch-manipulation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {step === 'type' ? t('createWizard.title') : t('createWizard.nameStep')}
          </h2>
          <button
            onClick={resetAndClose}
            className="p-2 -mr-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg touch-manipulation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-6 overflow-y-auto flex-1">
          {/* Error */}
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-red-100 dark:bg-red-900/30 rounded-xl text-red-700 dark:text-red-400">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium text-sm">{error}</span>
            </div>
          )}

          {/* Step 1: Form Type Selection */}
          {step === 'type' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('createWizard.selectType')}
              </p>
              {formTypes.map((ft) => (
                <button
                  key={ft.type}
                  onClick={() => handleTypeSelect(ft.type)}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition-all touch-manipulation active:scale-[0.98] ${
                    formType === ft.type
                      ? `${ft.borderColor} ${ft.bgColor} ring-2 ${ft.ringColor}`
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${ft.bgColor} ${ft.textColor}`}>
                      {ft.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${formType === ft.type ? ft.textColor : 'text-gray-900 dark:text-white'}`}>
                          {t(`formsPage.formTypes.${ft.type === 'live_quiz' ? 'liveQuiz' : ft.type}`)}
                        </h3>
                        {ft.badge && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium ${ft.bgColor} ${ft.textColor} rounded`}>
                            {ft.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {t(`createWizard.typeDescriptions.${ft.type === 'live_quiz' ? 'liveQuiz' : ft.type}`)}
                      </p>
                    </div>
                    <svg className={`w-5 h-5 ${ft.textColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Form Name */}
          {step === 'name' && selectedTypeConfig && (
            <div className="space-y-6">
              {/* Selected Type Preview */}
              <div className={`p-4 rounded-2xl ${selectedTypeConfig.bgColor} flex items-center gap-3`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-white/50 dark:bg-black/20 ${selectedTypeConfig.textColor}`}>
                  {selectedTypeConfig.icon}
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('createWizard.creating')}</p>
                  <p className={`font-medium ${selectedTypeConfig.textColor}`}>
                    {t(`formsPage.formTypes.${formType === 'live_quiz' ? 'liveQuiz' : formType}`)}
                  </p>
                </div>
              </div>

              {/* Form Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('createWizard.formName')}
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => {
                    setFormName(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={t('createWizard.namePlaceholder')}
                  autoFocus
                  className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-lg"
                />
              </div>

              {/* Create Button */}
              <button
                onClick={handleCreate}
                disabled={creating || !formName.trim()}
                className={`w-full py-4 rounded-2xl font-semibold text-white transition-all touch-manipulation active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedTypeConfig.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                  selectedTypeConfig.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700' :
                  selectedTypeConfig.color === 'green' ? 'bg-green-600 hover:bg-green-700' :
                  'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {creating ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('createWizard.creating')}...
                  </span>
                ) : (
                  t('createWizard.createButton')
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
